import React from 'react';
import './DocumentViewModal.css';

export interface DocumentForView {
  id: string;
  rawId?: number;
  restaurant: string;
  documentType: string;
  status: 'pending' | 'approved' | 'rejected' | 'needs-info';
  uploadDate: string;
  reviewer: string;
  hasInfo?: boolean;
  fileUrl?: string;
}

interface DocumentViewModalProps {
  document: DocumentForView | null;
  onClose: () => void;
  onApprove?: (document: DocumentForView) => void;
  onReject?: (document: DocumentForView) => void;
}

const DocumentViewModal: React.FC<DocumentViewModalProps> = ({
  document: doc,
  onClose,
  onApprove,
  onReject,
}) => {
  if (!doc) return null;

  const getStatusClass = (status: string) => status.toLowerCase().replace(' ', '-');
  const isPending = doc.status === 'pending';

  return (
    <div className="document-view-overlay" onClick={onClose}>
      <div className="document-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="document-view-header">
          <h2 className="document-view-title">Document Details</h2>
          <button type="button" className="document-view-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="document-view-body">
          <div className="document-view-info">
            <div className="document-view-row">
              <span className="document-view-label">Document ID</span>
              <span className="document-view-value">{doc.id}</span>
            </div>
            <div className="document-view-row">
              <span className="document-view-label">Restaurant</span>
              <span className="document-view-value">{doc.restaurant}</span>
            </div>
            <div className="document-view-row">
              <span className="document-view-label">Document Type</span>
              <span className="document-view-value">{doc.documentType}</span>
            </div>
            <div className="document-view-row">
              <span className="document-view-label">Status</span>
              <span className={`document-view-status status-${getStatusClass(doc.status)}`}>
                {doc.status === 'needs-info' ? 'Needs Info' : doc.status}
              </span>
            </div>
            <div className="document-view-row">
              <span className="document-view-label">Upload Date</span>
              <span className="document-view-value">{doc.uploadDate}</span>
            </div>
            <div className="document-view-row">
              <span className="document-view-label">Reviewer</span>
              <span className="document-view-value">{doc.reviewer || '—'}</span>
            </div>
          </div>

          {doc.fileUrl && (
            <div className="document-view-preview">
              <div className="document-view-label">Preview</div>
              <div className="document-view-embed">
                <iframe
                  src={doc.fileUrl}
                  title={`Document ${doc.id}`}
                  className="document-view-iframe"
                />
              </div>
              <a
                href={doc.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="document-view-open-link"
              >
                Open in new tab
              </a>
            </div>
          )}

          {!doc.fileUrl && (
            <div className="document-view-no-preview">
              No file preview available. Use the backend document URL if available.
            </div>
          )}
        </div>

        <div className="document-view-actions">
          {isPending && (
            <>
              <button
                type="button"
                className="document-view-btn document-view-approve"
                onClick={() => onApprove?.(doc)}
              >
                ✓ Approve
              </button>
              <button
                type="button"
                className="document-view-btn document-view-reject"
                onClick={() => onReject?.(doc)}
              >
                × Reject
              </button>
            </>
          )}
          <button type="button" className="document-view-btn document-view-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewModal;
